import { ADDITIONAL_INSURED_LANGUAGE_VERSION } from '../constants/additionalInsured';
import { HOST_TERMS_VERSION } from '../pages/RegisterPage/hostOnboardingConstants';

export const EMPTY_LEGAL_ENTITY = { legalName: '', address: '' };

// The Yes/No question is gone; every host is required to accept the
// Blanket Additional Insured election. `required` stays on the model for
// legacy drawer/summary readers, but the wizard treats acceptance itself as
// the primary gate (see `hasValidAdditionalInsuredAcceptance` below).
export const EMPTY_INSURANCE = {
    required: true,
    legalEntities: [{ ...EMPTY_LEGAL_ENTITY }],
};

function defaultLegalEntities(raw) {
    if (Array.isArray(raw?.legalEntities) && raw.legalEntities.length > 0) {
        return raw.legalEntities.map((e) => ({
            legalName: String(e?.legalName ?? ''),
            address: String(e?.address ?? ''),
        }));
    }
    return [{ ...EMPTY_LEGAL_ENTITY }];
}

function normalizeAcceptance(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (!raw.acceptanceRecordId || !raw.languageVersion || !raw.hostAgreementVersion) return null;
    return {
        acceptanceRecordId: String(raw.acceptanceRecordId),
        acceptedAt: raw.acceptedAt ?? null,
        languageVersion: String(raw.languageVersion),
        hostAgreementVersion: String(raw.hostAgreementVersion),
        checkboxTextSnapshot: raw.checkboxTextSnapshot ?? '',
        hostLegalEntityName: raw.hostLegalEntityName ?? '',
        authorizedUserId: raw.authorizedUserId ?? null,
        authorizedUserName: raw.authorizedUserName ?? '',
        authorizedUserEmail: raw.authorizedUserEmail ?? '',
        ipAddress: raw.ipAddress ?? null,
        userAgent: raw.userAgent ?? null,
    };
}

export function normalizeInsurance(raw) {
    if (!raw || typeof raw !== 'object') {
        return { ...EMPTY_INSURANCE, legalEntities: [{ ...EMPTY_LEGAL_ENTITY }] };
    }

    const acceptance = normalizeAcceptance(raw.additionalInsuredAcceptance);
    const base = {
        legalEntities: defaultLegalEntities(raw),
        additionalInsuredAcceptance: acceptance ?? undefined,
    };

    if (raw.required === false || raw.notNeeded === true) {
        return { required: false, ...base };
    }
    if (raw.required === null) {
        return { required: null, ...base };
    }
    if (raw.required === true) {
        return { required: true, ...base };
    }

    return { ...EMPTY_INSURANCE, legalEntities: [{ ...EMPTY_LEGAL_ENTITY }] };
}

export function normalizeWizardInsurance(raw) {
    if (!raw || typeof raw !== 'object') {
        return { ...EMPTY_INSURANCE, legalEntities: [{ ...EMPTY_LEGAL_ENTITY }] };
    }
    // Wizard treats null (server has no answer yet) as ready-to-accept.
    if (raw.required === null || raw.required === undefined) {
        const acceptance = normalizeAcceptance(raw.additionalInsuredAcceptance);
        return {
            ...EMPTY_INSURANCE,
            legalEntities: defaultLegalEntities(raw),
            additionalInsuredAcceptance: acceptance ?? undefined,
        };
    }
    return normalizeInsurance(raw);
}

export function normalizeDrawerInsurance(listing) {
    const raw = listing?.insurance;
    if (!raw || typeof raw !== 'object') {
        return { required: null, legalEntities: [{ ...EMPTY_LEGAL_ENTITY }] };
    }
    return normalizeInsurance(raw);
}

// The generic PUT strips additionalInsuredAcceptance server-side,
// but there's no reason to send it either. Keep client payloads to the
// mutable subset only.
export function insuranceToPayload(insurance) {
    const ins = normalizeInsurance(insurance);

    if (ins.required === null) {
        return {
            required: null,
            legalEntities: ins.legalEntities.map((e) => ({
                legalName: e.legalName.trim(),
                address: e.address.trim(),
            })),
        };
    }

    return {
        required: ins.required === true,
        legalEntities: ins.legalEntities.map((e) => ({
            legalName: e.legalName.trim(),
            address: e.address.trim(),
        })),
    };
}

/**
 * Payload copied to all bulk drafts (per v7 apply-all).
 * The acceptance snapshot is a per-listing legal artifact and MUST
 * NOT be copied across listings. Every listing's host must accept
 * independently. `insuranceToPayload` already excludes acceptance, so this
 * inherits that guarantee.
 */
export function insuranceApplyAllPayload(insurance) {
    const ins = normalizeInsurance(insurance);
    if (ins.required === null) {
        return null;
    }
    return insuranceToPayload(ins);
}

/**
 * Blanket Additional Insured acceptance is present AND both stored
 * versions match the current client constants. Client mirror of
 * server/helpers/insuranceAcceptance.js.
 */
export function hasValidAdditionalInsuredAcceptance(insurance) {
    const snap = insurance?.additionalInsuredAcceptance;
    if (!snap || typeof snap !== 'object') return false;
    if (!snap.acceptanceRecordId) return false;
    if (snap.languageVersion !== ADDITIONAL_INSURED_LANGUAGE_VERSION) return false;
    if (snap.hostAgreementVersion !== HOST_TERMS_VERSION) return false;
    return true;
}

/**
 * Bulk readiness. A host who explicitly opts out (required === false) has
 * addressed the step just as fully as one who accepts — either answer resolves
 * it. Otherwise a current acceptance snapshot is required.
 */
export function isInsuranceAddressed(insurance) {
    if (!insurance || typeof insurance !== 'object') return false;
    if (insurance.required === false) return true;
    return hasValidAdditionalInsuredAcceptance(insurance);
}

export function isInsuranceUnresolved(insurance) {
    return !isInsuranceAddressed(insurance);
}
